import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordToggle } from '@/components/ui/PasswordToggle';
import { supabase } from '@/lib/supabase';
import { RECOVERY_VERIFIED_KEY } from '@/lib/auth-recovery';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/reset-password')({
  component: ResetPassword,
});

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // One switch drives both fields — the point of revealing here is to check the
  // two match, which is impossible if they unmask independently.
  const [showPasswords, setShowPasswords] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Only a verified recovery flow may change a password here. Without this
  // gate, updateUser() acted on whatever session was ambient — including a
  // persisted session for a DIFFERENT account than the reset email's target.
  // Verified means: /auth/confirm validated a recovery token_hash this tab
  // (sessionStorage flag), or supabase-js emitted PASSWORD_RECOVERY (legacy
  // {{ .ConfirmationURL }} links that land here directly).
  const [recoveryVerified, setRecoveryVerified] = useState(
    () => sessionStorage.getItem(RECOVERY_VERIFIED_KEY) === '1',
  );
  const navigate = useNavigate();

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        sessionStorage.setItem(RECOVERY_VERIFIED_KEY, '1');
        setRecoveryVerified(true);
      } else if (event === 'SIGNED_OUT') {
        // The verified flag belongs to the recovery session that earned it.
        // If that session ends (sign-out, refresh failure), a later sign-in
        // by a DIFFERENT account in this tab must not inherit an unlocked
        // reset form.
        sessionStorage.removeItem(RECOVERY_VERIFIED_KEY);
        setRecoveryVerified(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!recoveryVerified) {
      setError('This page can only be used from a password-reset email link. Please request a new one.');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      sessionStorage.removeItem(RECOVERY_VERIFIED_KEY);
      setSuccess(true);
      setTimeout(() => {
        navigate({ to: '/' });
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute -top-[30%] -left-[10%] w-[70%] h-[70%] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute top-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-secondary/20 blur-3xl" />
      </div>

      <Card className="w-full max-w-md z-10 border-border bg-card/80 backdrop-blur-sm shadow-xl">
        <CardHeader className="text-center space-y-2 pb-6">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-2">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-3xl font-bold tracking-tight text-foreground">
            Reset Password
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            Enter your new password below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="p-4 text-center space-y-4">
              <div className="text-green-500 font-medium">
                Password updated successfully! Redirecting you to your dashboard...
              </div>
              <Button onClick={() => navigate({ to: '/' })} className="w-full">
                Go to Dashboard
              </Button>
            </div>
          ) : !recoveryVerified ? (
            <div className="p-4 text-center space-y-4">
              <div className="text-sm text-muted-foreground">
                This page can only be used from a password-reset email link. If your
                link has expired, request a new one from the sign-in page
                (&quot;Forgot password?&quot;).
              </div>
              <Button onClick={() => navigate({ to: '/login' })} className="w-full">
                Go to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-4 text-sm font-medium text-destructive bg-destructive/10 rounded-xl border border-destructive/20 flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    placeholder="New Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 pr-11 h-10 bg-background/50 border-input focus:bg-background transition-all"
                  />
                  <PasswordToggle
                    visible={showPasswords}
                    onToggle={() => setShowPasswords((v) => !v)}
                    label="new password"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    placeholder="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="pl-10 pr-11 h-10 bg-background/50 border-input focus:bg-background transition-all"
                  />
                  <PasswordToggle
                    visible={showPasswords}
                    onToggle={() => setShowPasswords((v) => !v)}
                    label="confirmed password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-10 text-base font-semibold shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300"
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
